import {sleep} from '@benchling/js-utils';
import {v4 as uuidv4} from 'uuid';

import EntryActions from 'actions/EntryActions';
import * as analytics from 'analytics/Analytics';
import {MaybeArchivableItem} from 'archive/ArchiveConstants';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {
  InaccessibleInfoNode,
  ProcessExecutionType,
  ProcessTaskStatusType,
  PublishingStatus,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ItemListingQuery} from 'nav/sidepanel/listing/__generated/ItemListingQuery';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  PROCESSES_EXECUTE_INTO_ENTRY_STARTED_EVENT,
  createProcessEventMetadata,
  PROCESSES_EXECUTE_INTO_ENTRY_ENDED_EVENT,
} from 'performance/processProcessesEvents';
import * as ProcessManagementDAO from 'processes/dao/ProcessManagementDAO';
import {compareTaskGroupOrder} from 'processes/dashboard/ExecuteButton';
import ExecuteTasksModal from 'processes/modals/ExecuteTasksModal';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

import {ProcessOutputGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessOutputGraphQL';
import {ProcessTaskGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskGraphQL';
import DirectExecutionModal from '../modals/DirectExecutionModal';
import {invalidateInDashboard} from '../ProcessManagementActions';

type ProcessTaskSchema = ExtractByTypename<
  ItemListingQuery['processTasks'][number],
  'ProcessTaskNode'
>['schema'];

// Minimal interface for executing a process task.
export interface ExecutableProcessTask extends MaybeArchivableItem {
  __typename: 'ProcessTaskNode';
  id: StrictString;
  schema: ProcessTaskSchema;
  process: {id: StrictString};
  status: {
    statusType: ProcessTaskStatusType;
  };
  sortValue: StrictString;
  procedureMethodExecutionInstance:
    | {
        worksheet: {__typename: 'WorksheetNode'; editUrl: string} | InaccessibleInfoNode;
        __typename?: 'ProcedureMethodExecutionInstanceNode';
      }
    | null
    | InaccessibleInfoNode;
  procedureStepExecutionInstance:
    | {
        document: {__typename: 'DocumentNode'; editUrl: string} | InaccessibleInfoNode;
        __typename?: 'ProcedureStepExecutionInstanceNode';
      }
    | null
    | InaccessibleInfoNode;
}

async function runProcessTaskDirectExecution(
  selectedTasks: Array<ExecutableProcessTask>,
  onTasksOrOutputsChange?: (tasks: Array<ProcessTaskGraphQL>, outputs?: Array<ProcessOutputGraphQL>) => void,
  actionOrigin?: ActionOrigin
): Promise<void> {
  const taskIds = selectedTasks.map(({id}) => id);
  const directExecutionUuid = uuidv4();
  let modalPromise: Promise<unknown> = Promise.resolve();
  await withLoadingScreen(
    (async () => {
      const {tasks} = await ProcessManagementDAO.startDirectExecution({
        taskIds,
        directExecutionUuid,
        actionOrigin,
      });
      const processes = [...new Set(tasks.map(({process}) => process.id))].map((id) => ({id}));
      invalidateInDashboard([...tasks, ...processes]);
      onTasksOrOutputsChange?.(tasks);
      // We initiate the modal here, but don't await its return. This lets the loading screen fall off,
      // without a flash before the modal appears.
      modalPromise = runModal(DirectExecutionModal, {
        taskIds,
        directExecutionUuid,
        onTasksOrOutputsChange,
      });
      // This also protects against a flash between the loading screen and the modal.
      await sleep(100);
    })(),
    'Executing tasks'
  );
  await modalPromise;
}

async function runProcessTaskEntryExecution(
  selectedItems: Array<ExecutableProcessTask>,
  actionOrigin?: ActionOrigin
): Promise<void> {
  const taskIds = selectedItems.map((task) => task.id);
  let entryFolderId: string | null = null;
  const defualtExecutionFolder = selectedItems[0].schema.defaultEntryExecutionFolderOrInaccessible;
  const clientOrigin = selectedItems[0].schema.clientOrigin;
  assert(
    selectedItems.every((item) => item.schema.clientOrigin === clientOrigin),
    'clientOrigin does not match accross selected items in entry execution'
  );
  if (defualtExecutionFolder == null || isInaccessible(defualtExecutionFolder)) {
    entryFolderId = FolderDefaults.get(FolderDefaultsKey.PROCESS_EXECUTION_ENTRY) ?? null;
  } else {
    entryFolderId = defualtExecutionFolder.id;
  }

  const executionParameters = await runModal(ExecuteTasksModal, {
    taskIds,
    entryTemplate: selectedItems[0].schema.entryTemplate!,
    entryFolderId,
    actionOrigin,
    clientOrigin,
  });
  if (executionParameters != null) {
    const {folderId} = executionParameters as {folderId?: string};
    if (folderId != null) {
      FolderDefaults.set(FolderDefaultsKey.PROCESS_EXECUTION_ENTRY, folderId);
    }

    const profileEventMetadata = createProcessEventMetadata({
      numItems: taskIds.length,
      actionOrigin: actionOrigin ?? ActionOrigin.UI_UNDETERMINED,
    });
    const profileEventId = uuidv4();
    logProfilerTimingEvent(
      {...PROCESSES_EXECUTE_INTO_ENTRY_STARTED_EVENT, id: profileEventId},
      profileEventMetadata
    );
    const apiResult = await ProcessManagementDAO.executeProcessTasks(executionParameters);
    const isExistingEntry = ProcessManagementDAO.isExecuteIntoExistingEntryParameters(executionParameters);
    if (isExistingEntry) {
      assert(
        apiResult.entryId === executionParameters.entryId,
        'Expected API result entry to match submitted entry',
        {apiEntryId: apiResult.entryId, submittedEntryId: executionParameters.entryId}
      );
      // Executing the tasks into an existing entry will invalidate the entry that's currently in the Redux
      // store. So we refetch the entry into the store on successful execution.
      // For Table Mapping, we want to ensure that we actually wait for the Entry to have finished opening
      // before opening in the file browser.
      await EntryActions.open(apiResult.entryId);
    }
    FileBrowserActions.openFile(apiResult.entryId, {
      onOpen: () => {
        EntryActions.invokeProcessTaskExecutionTableMapping(apiResult.entryId, taskIds);
      },
    });
    logProfilerTimingEvent(
      {...PROCESSES_EXECUTE_INTO_ENTRY_ENDED_EVENT, id: profileEventId},
      profileEventMetadata
    );
  }
}

export async function runProcessTaskExecution(
  selectedTasks: Array<ExecutableProcessTask>,
  onTasksOrOutputsChange?: (tasks: Array<ProcessTaskGraphQL>, outputs?: Array<ProcessOutputGraphQL>) => void,
  actionOrigin?: ActionOrigin
): Promise<void> {
  assert(selectedTasks.length > 0, 'Expected at least one task');

  if (selectedTasks[0].schema.entryTemplate != null) {
    await runProcessTaskEntryExecution(selectedTasks, actionOrigin);
  } else {
    await runProcessTaskDirectExecution(selectedTasks, onTasksOrOutputsChange, actionOrigin);
  }
}

export async function executeProcessTasksFromSearch(
  executableTasks: Array<ExecutableProcessTask>,
  onTasksOrOutputsChange?: (tasks: Array<ProcessTaskGraphQL>, outputs?: Array<ProcessOutputGraphQL>) => void,
  actionOrigin?: ActionOrigin
): Promise<void> {
  if (BenchlingConfig.WORKFLOWS_ENABLE_DASHBOARD_EXECUTION_TASK_GROUP_ORDERING) {
    executableTasks.sort(compareTaskGroupOrder);
  }

  await runProcessTaskExecution(executableTasks, onTasksOrOutputsChange, actionOrigin);
}

export function getWorksheetUrlFromProcessTask(executableTask: ExecutableProcessTask): string | null {
  let worksheetUrl = null;
  if (
    executableTask.procedureMethodExecutionInstance != null &&
    !isInaccessible(executableTask.procedureMethodExecutionInstance) &&
    !isInaccessible(executableTask.procedureMethodExecutionInstance.worksheet)
  ) {
    worksheetUrl = executableTask.procedureMethodExecutionInstance.worksheet?.editUrl;
  } else if (
    executableTask.procedureStepExecutionInstance != null &&
    !isInaccessible(executableTask.procedureStepExecutionInstance) &&
    !isInaccessible(executableTask.procedureStepExecutionInstance.document)
  ) {
    worksheetUrl = executableTask.procedureStepExecutionInstance.document?.editUrl;
  } else {
    return null;
  }

  assert(worksheetUrl != null, 'Expected worksheet url to be defined');
  return worksheetUrl;
}

export function openProcessTaskWithWorksheet(
  executableTask: ExecutableProcessTask,
  actionOrigin: ActionOrigin = ActionOrigin.UI_UNDETERMINED
): void {
  const worksheetUrl = getWorksheetUrlFromProcessTask(executableTask);
  if (worksheetUrl != null) {
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.EXECUTED_TASK_INTO_WORKSHEET, {
      action_origin: actionOrigin,
      client_origin: executableTask.schema.clientOrigin,
      task_api_id: executableTask.id,
      task_schema_type: executableTask.schema.executionType,
    });
    BenchlingUtil.navigate(null, worksheetUrl);
  }
}

interface SchemaWithPublishingRecord {
  executionType: ProcessExecutionType;
  entryTemplate: null | ExtractByTypename<
    ExtractByTypename<ItemListingQuery['processTasks'][number], 'ProcessTaskNode'>['schema']['entryTemplate'],
    'TemplateNode'
  >;
}
export function isExecutionBlockedByTemplatePublishing(schema: SchemaWithPublishingRecord): boolean {
  if (BenchlingConfig.ELN_ENABLE_TEMPLATE_PUBLISHING === false) {
    return false;
  } else if (schema.executionType !== ProcessExecutionType.ENTRY) {
    return false;
  }
  const currentPublishingRecord = schema.entryTemplate!.currentPublishingRecord;
  if (currentPublishingRecord == null) {
    // Template has never been published
    return true;
  } else if (
    isInaccessible(currentPublishingRecord) ||
    currentPublishingRecord.publishingStatus !== PublishingStatus.EFFECTIVE
  ) {
    // Template has been published but the current publishing status is not Effective
    return true;
  } else {
    return false;
  }
}
